import React from 'react';
import { Plus, X, Loader } from 'lucide-react';
import { useAskSidebar, type TabState } from '../../contexts/AskSidebarContext';

interface Props {
  caseId: string;
}

export const AgentTabBar: React.FC<Props> = () => {
  const { tabs, tabOrder, activeTabId, createTab, closeTab, switchTab } = useAskSidebar();

  const tabEntries: TabState[] = tabOrder
    .map(id => tabs.get(id))
    .filter((t): t is TabState => !!t);

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 bg-gray-50 dark:bg-medical-850 border-b border-gray-200 dark:border-medical-700 overflow-x-auto scrollbar-none">
      {tabEntries.map(tab => {
        const isActive = tab.tabId === activeTabId;
        const isRunning = tab.isLoading || tab.isStreaming;

        return (
          <div
            key={tab.tabId}
            onClick={() => switchTab(tab.tabId)}
            className={`group relative flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-xs cursor-pointer transition-colors min-w-0 max-w-[180px] ${
              isActive
                ? 'bg-white dark:bg-medical-800 text-gray-800 dark:text-gray-200 border-b-2 border-purple-500 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-medical-700 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {isRunning && (
              <Loader className="h-3 w-3 flex-shrink-0 animate-spin text-purple-500" />
            )}
            <span className="truncate font-medium">
              {tab.title.length > 25 ? tab.title.substring(0, 25) + '...' : tab.title}
            </span>
            {tabOrder.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.tabId);
                }}
                className={`p-0.5 rounded flex-shrink-0 transition-opacity ${
                  isActive
                    ? 'text-gray-400 hover:text-red-500 dark:hover:text-red-400'
                    : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 dark:hover:text-red-400'
                }`}
                title="Close tab"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}

      {/* New tab button */}
      <button
        onClick={() => createTab()}
        className="p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-medical-700 rounded transition-colors flex-shrink-0"
        title="New conversation tab"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
